import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "react-query";
import {
  editCompanyBranch,
  checkOnlineOrderingStatus,
} from "../../../service";
import { useBranchContext } from "../../../contexts/branch-context";
import {
  Button,
  Flex,
  Input,
  message,
  Modal,
  notification,
  Spin,
  Switch,
  DatePicker,
} from "antd";
import { Clock, CalendarX, X, PlusCircle } from "@phosphor-icons/react";
import styled from "styled-components";
import { colors } from "../../../styles/theme";

import dayjs from "dayjs";
import { AgreementModal } from "../agreement-modal/AgreementModal";
import {
  SectionTitle,
  StyledSwitch,
  SettingItem,
  SettingRow,
  SettingContent,
  SettingLabel,
  SettingDescription
} from "../../../styles/shared-components";

const ActionButton = styled(Button)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border-radius: 8px;
  border: 1px solid ${colors.primary};
  color: ${colors.primary};
  background: var(--color-surface);
  margin-top: 8px;

  &:hover {
    background: ${colors.primaryLight} !important;
    border-color: ${colors.primary} !important;
    color: ${colors.primary} !important;
  }
`;

// Day avatar circle (S, M, T, W, Th, F)
const DayAvatar = styled.div<{ $enabled: boolean }>`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${({ $enabled }) => ($enabled ? colors.primary : "#e8edf0")};
  color: ${({ $enabled }) => ($enabled ? "#fff" : colors.textSecondary)};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 600;
  flex-shrink: 0;
`;

const DayRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 16px 0;

  &:last-child {
    border-bottom: none;
  }
`;

// Wiersz dnia: [avatar] [X] [input – input] [+]
const DayLine = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
`;

const UnavailableLabel = styled.span`
  color: ${colors.textSecondary};
  font-size: 14px;
  flex: 1;
`;

const TimeRangeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
`;

const TimeInput = styled(Input)`
  flex: 1;
  min-width: 60px;
`;

const TimeSeparator = styled.span`
  color: ${colors.textSecondary};
  font-size: 16px;
  flex-shrink: 0;
`;

const IconButton = styled(Button)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  flex-shrink: 0;
`;

const AddRangeIconButton = styled(IconButton)`
  color: ${colors.primary};
  &:hover {
    color: ${colors.primary} !important;
    background: ${colors.primaryLight} !important;
  }
`;

const RemoveRangeIconButton = styled(IconButton)`
  color: ${colors.textSecondary};
  &:hover {
    color: ${colors.error} !important;
    background: transparent !important;
  }
`;

// Pusty spacer zachowujący miejsce ikony
const IconSpacer = styled.div`
  flex-shrink: 0;
`;

const dayAbbreviationKeys: Record<string, string> = {
  monday: "dayAbbrMonday",
  tuesday: "dayAbbrTuesday",
  wednesday: "dayAbbrWednesday",
  thursday: "dayAbbrThursday",
  friday: "dayAbbrFriday",
  saturday: "dayAbbrSaturday",
  sunday: "dayAbbrSunday",
};

export const OnlineSalesSettings: React.FC = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSecondModalOpen, setIsSecondModalOpen] = useState(false);
  const [isAgreementModalOpen, setIsAgreementModalOpen] = useState(false);
  const [selectedBranchId, setSelectedBranchId] = useState<number | null>(null);

  const [openingHours, setOpeningHours] = useState({
    monday: [],
    tuesday: [],
    wednesday: [],
    thursday: [],
    friday: [],
    saturday: [],
    sunday: [],
  });

  const [availabilityExceptions, setAvailabilityExceptions] = useState<{
    exceptionDates: string[];
    saturday: boolean;
    sunday: boolean;
  }>({
    exceptionDates: [],
    saturday: false,
    sunday: false,
  });

  const { currentBranch } = useBranchContext();

  useEffect(() => {
    if (currentBranch) {
      // Completely replace state with new branch data
      setOpeningHours({
        monday: [],
        tuesday: [],
        wednesday: [],
        thursday: [],
        friday: [],
        saturday: [],
        sunday: [],
        ...currentBranch.openingHours,
      });
      setAvailabilityExceptions({
        exceptionDates: [],
        saturday: false,
        sunday: false,
        ...currentBranch.futureOrderAvailabilityExceptions,
      });
    }
  }, [currentBranch, currentBranch?.id, currentBranch?.openingHours, currentBranch?.futureOrderAvailabilityExceptions]);

  const {
    mutateAsync: updateCompanyBranchAsync,
    isLoading: updateCompanyBranchAsyncIsLoading,
  } = useMutation(editCompanyBranch, {
    onSuccess: () => {
      setIsModalOpen(false);
      setIsSecondModalOpen(false);
      setSelectedBranchId(null);
      // Don't reset openingHours here - let useEffect handle it after data refresh
      queryClient.invalidateQueries([`/company-branches`]);
      notification.success({
        message: t("onlineSalesSettings.changesSaved"),
        style: { background: colors.successBackground },
      });
    },
    onError: () => {
      queryClient.invalidateQueries([`/company-branches`]);
      notification.error({
        message: t("onlineSalesSettings.error"),
        style: { background: colors.errorBackground },
      });
    },
  });

  const showModal = (id: number) => {
    setIsModalOpen(true);
    setSelectedBranchId(id);
    if (currentBranch) {
      setOpeningHours({
        monday: [],
        tuesday: [],
        wednesday: [],
        thursday: [],
        friday: [],
        saturday: [],
        sunday: [],
        ...currentBranch.openingHours,
      });
    }
  };

  const showSecondModal = (id: number) => {
    setIsSecondModalOpen(true);
    setSelectedBranchId(id);
    if (currentBranch) {
      setAvailabilityExceptions({
        exceptionDates: [],
        saturday: false,
        sunday: false,
        ...currentBranch.futureOrderAvailabilityExceptions,
      });
    }
  };

  const handleOk = () => {
    updateCompanyBranchAsync({
      id: selectedBranchId,
      data: {
        openingHours: openingHours,
      },
    });
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setSelectedBranchId(null);
    setIsSecondModalOpen(false);
    queryClient.invalidateQueries([`/company-branch`]);
  };

  const addRange = (day: string) => {
    setOpeningHours((current: any) => ({
      ...current,
      [day]: [...current[day], { from: "8:00", till: "16:00" }],
    }));
  };

  const editRange = (day: string, index: number, value: any) => {
    setOpeningHours((current: any) => {
      const newArray = [...current[day]];
      newArray[index] = value;
      return {
        ...current,
        [day]: newArray,
      };
    });
  };

  const removeRange = (day: string) => {
    setOpeningHours((current: any) => {
      const newArray = [...current[day]];
      newArray.pop();
      return {
        ...current,
        [day]: newArray,
      };
    });
  };

  const removeSpecificRange = (day: string, index: number) => {
    setOpeningHours((current: any) => {
      const newArray = [...current[day]];
      newArray.splice(index, 1);
      return {
        ...current,
        [day]: newArray,
      };
    });
  };

  const isDayEnabled = (day: string) => {
    return openingHours[day as keyof typeof openingHours]?.length > 0;
  };

  const toggleDay = (day: string, enabled: boolean) => {
    if (enabled) {
      addRange(day);
    } else {
      setOpeningHours((current: any) => ({
        ...current,
        [day]: [],
      }));
    }
  };

  const updateTimeRange = (day: string, index: number, field: 'from' | 'till', time: string) => {
    setOpeningHours((current: any) => {
      const newArray = [...current[day]];
      newArray[index] = {
        ...newArray[index],
        [field]: time,
      };
      return {
        ...current,
        [day]: newArray,
      };
    });
  };

  const handleTimeInput = (value: string, day: string, index: number, field: 'from' | 'till') => {
    // Usuń wszystko oprócz cyfr
    let cleaned = value.replace(/[^\d]/g, '');

    // Ogranicz do 4 cyfr (HHMM)
    if (cleaned.length > 4) {
      cleaned = cleaned.slice(0, 4);
    }

    // Formatuj jako HH:MM
    let formatted = cleaned;
    if (cleaned.length >= 3) {
      formatted = cleaned.slice(0, 2) + ':' + cleaned.slice(2);
    }

    // Waliduj godziny i minuty
    if (cleaned.length >= 2) {
      const hours = parseInt(cleaned.slice(0, 2));
      if (hours > 23) {
        formatted = '23' + (cleaned.length > 2 ? ':' + cleaned.slice(2) : '');
      }
    }

    if (cleaned.length === 4) {
      const minutes = parseInt(cleaned.slice(2, 4));
      if (minutes > 59) {
        formatted = cleaned.slice(0, 2) + ':59';
      }
    }

    updateTimeRange(day, index, field, formatted);
  };

  const normalizeTime = (value: string, day: string, index: number, field: 'from' | 'till') => {
    const digits = value.replace(/[^\d]/g, '');
    if (!digits) return;
    let hours: number;
    let minutes = 0;
    if (digits.length <= 2) {
      hours = parseInt(digits, 10);
    } else {
      hours = parseInt(digits.slice(0, 2), 10);
      minutes = parseInt(digits.slice(2, 4).padEnd(2, '0'), 10);
    }
    if (hours > 23) hours = 23;
    if (minutes > 59) minutes = 59;
    updateTimeRange(day, index, field, `${hours}:${String(minutes).padStart(2, '0')}`);
  };

  if (!currentBranch) {
    return <Spin />;
  }

  return (
    <>
      <SectionTitle>{t("onlineSalesSettings.sectionTitle")}</SectionTitle>

      {/* Zamówienia online */}
      <SettingItem>
        <SettingRow>
          <StyledSwitch
            onChange={async (value) => {
              if (!value) {
                updateCompanyBranchAsync({
                  id: currentBranch.id,
                  data: { isOpen: false },
                });
              } else {
                try {
                  const status = await checkOnlineOrderingStatus();
                  if (status.status === "signed") {
                    updateCompanyBranchAsync({
                      id: currentBranch.id,
                      data: { isOpen: true },
                    });
                  } else {
                    setIsAgreementModalOpen(true);
                  }
                } catch (error) {
                  console.error("Error checking agreement status:", error);
                  notification.error({
                    message: t("onlineSalesSettings.error"),
                    description: t("onlineSalesSettings.checkAgreementStatusError"),
                    style: { background: colors.errorBackground },
                  });
                }
              }
            }}
            checked={currentBranch.isOpen}
            loading={updateCompanyBranchAsyncIsLoading}
            disabled={updateCompanyBranchAsyncIsLoading}
          />
          <SettingContent>
            <SettingLabel>{t("onlineSalesSettings.onlineOrdersLabel")}</SettingLabel>
            <SettingDescription>
              {t("onlineSalesSettings.onlineOrdersDescription")}
            </SettingDescription>
            <ActionButton onClick={() => showModal(currentBranch.id)}>
              <Clock size={16} />
              {t("onlineSalesSettings.onlineOrderHours")}
            </ActionButton>
          </SettingContent>
        </SettingRow>
      </SettingItem>

      {/* Zamówienia przez kod QR */}
      <SettingItem>
        <SettingRow>
          <StyledSwitch
            onChange={(value) => {
              updateCompanyBranchAsync({
                id: currentBranch.id,
                data: { qrCodesActive: value },
              });
            }}
            checked={currentBranch.qrCodesActive && false}
            loading={updateCompanyBranchAsyncIsLoading}
            disabled={updateCompanyBranchAsyncIsLoading || true}
          />
          <SettingContent>
            <SettingLabel>{t("onlineSalesSettings.qrOrdersLabel")}</SettingLabel>
            <SettingDescription>
              {t("onlineSalesSettings.qrOrdersDescription")}
            </SettingDescription>
          </SettingContent>
        </SettingRow>
      </SettingItem>

      {/* Zamówienia "na później" */}
      <SettingItem>
        <SettingRow>
          <StyledSwitch
            onChange={(value) => {
              if (!currentBranch.isOpen) {
                message.warning(t("onlineSalesSettings.enableOnlineOrderingFirst"));
                return;
              }
              updateCompanyBranchAsync({
                id: currentBranch.id,
                data: { allowFutureOrders: value },
              });
            }}
            checked={currentBranch.allowFutureOrders}
            loading={updateCompanyBranchAsyncIsLoading}
            disabled={updateCompanyBranchAsyncIsLoading || !currentBranch.isOpen}
          />
          <SettingContent>
            <SettingLabel>{t("onlineSalesSettings.futureOrdersLabel")}</SettingLabel>
            <SettingDescription>
              {t("onlineSalesSettings.futureOrdersDescription")}
            </SettingDescription>
            <ActionButton onClick={() => showSecondModal(currentBranch.id)}>
              <CalendarX size={16} />
              {t("onlineSalesSettings.unavailableDaysForFutureOrders")}
            </ActionButton>
          </SettingContent>
        </SettingRow>
      </SettingItem>

      {/* Zamówienia "na teraz" */}
      <SettingItem>
        <SettingRow>
          <StyledSwitch
            onChange={(value) => {
              if (!currentBranch.isOpen) {
                message.warning(t("onlineSalesSettings.enableOnlineOrderingFirst"));
                return;
              }
              updateCompanyBranchAsync({
                id: currentBranch.id,
                data: { allowCurrentOrders: value },
              });
            }}
            checked={currentBranch.allowCurrentOrders}
            loading={updateCompanyBranchAsyncIsLoading}
            disabled={updateCompanyBranchAsyncIsLoading || !currentBranch.isOpen}
          />
          <SettingContent>
            <SettingLabel>{t("onlineSalesSettings.currentOrdersLabel")}</SettingLabel>
            <SettingDescription>
              {t("onlineSalesSettings.currentOrdersDescription")}
            </SettingDescription>
          </SettingContent>
        </SettingRow>
      </SettingItem>

      {/* Modal godzin przyjmowania zamówień */}
      <Modal
        width={500}
        title={t("onlineSalesSettings.orderHoursModalTitle")}
        open={isModalOpen}
        onOk={handleOk}
        onCancel={handleCancel}
        okText={t("onlineSalesSettings.save")}
        cancelText={t("onlineSalesSettings.cancel")}
        confirmLoading={updateCompanyBranchAsyncIsLoading}
        centered
      >
        <div style={{ marginTop: "16px" }}>
          {Object.entries(openingHours).map(([day, ranges]: [string, any]) => {
            const enabled = isDayEnabled(day);
            return (
              <DayRow key={day}>
                {/* Układ: [avatar] [X] [input–input] [+] — spacery zachowują kolumny */}

                {!enabled ? (
                  // Dzień wyłączony: [avatar] [label] [+] [spacer-X]
                  <DayLine>
                    <DayAvatar $enabled={false}>{t(`onlineSalesSettings.${dayAbbreviationKeys[day]}`)}</DayAvatar>
                    <UnavailableLabel>{t("onlineSalesSettings.salesUnavailable")}</UnavailableLabel>
                    <AddRangeIconButton
                      type="text"
                      icon={<PlusCircle size={22} weight="regular" />}
                      onClick={() => addRange(day)}
                    />
                    <IconSpacer />
                  </DayLine>
                ) : (
                  // Dzień aktywny: [avatar/spacer] [inputy] [+/spacer] [X]
                  ranges.map((range: any, i: number) => (
                    <DayLine key={i}>
                      {/* Kolumna 1: avatar (tylko pierwszy wiersz) lub spacer */}
                      {i === 0
                        ? <DayAvatar $enabled={true}>{t(`onlineSalesSettings.${dayAbbreviationKeys[day]}`)}</DayAvatar>
                        : <IconSpacer />
                      }
                      {/* Kolumna 2: inputy */}
                      <TimeRangeRow>
                        <TimeInput
                          value={range.from || ''}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleTimeInput(e.target.value, day, i, 'from')}
                          onBlur={(e: React.FocusEvent<HTMLInputElement>) => normalizeTime(e.target.value, day, i, 'from')}
                          placeholder="HH:MM"
                          maxLength={5}
                        />
                        <TimeSeparator>–</TimeSeparator>
                        <TimeInput
                          value={range.till || ''}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleTimeInput(e.target.value, day, i, 'till')}
                          onBlur={(e: React.FocusEvent<HTMLInputElement>) => normalizeTime(e.target.value, day, i, 'till')}
                          placeholder="HH:MM"
                          maxLength={5}
                        />
                      </TimeRangeRow>
                      {/* Kolumna 3: + tylko w pierwszym wierszu, spacer w pozostałych */}
                      {i === 0
                        ? <AddRangeIconButton
                          type="text"
                          icon={<PlusCircle size={22} weight="regular" />}
                          onClick={() => addRange(day)}
                        />
                        : <IconSpacer />
                      }
                      {/* Kolumna 4: X zawsze */}
                      <RemoveRangeIconButton
                        type="text"
                        icon={<X size={18} weight="regular" />}
                        onClick={() => removeSpecificRange(day, i)}
                      />
                    </DayLine>
                  ))
                )}
              </DayRow>
            );
          })}
        </div>
      </Modal>

      {/* Modal wyłączonych dat realizacji */}
      <Modal
        width={400}
        title={t("onlineSalesSettings.unavailableDaysForFutureOrders")}
        open={isSecondModalOpen}
        onOk={() => {
          updateCompanyBranchAsync({
            id: selectedBranchId,
            data: {
              futureOrderAvailabilityExceptions: availabilityExceptions,
            },
          });
        }}
        onCancel={handleCancel}
      >
        <Flex
          style={{
            paddingLeft: 10,
            paddingTop: 20,
            paddingBottom: 10,
          }}
          gap={30}
          vertical
        >
          <Flex align="center" gap={12}>
            <Switch
              onChange={(value) => {
                setAvailabilityExceptions((current: any) => ({
                  ...current,
                  saturday: value,
                }));
              }}
              checked={availabilityExceptions.saturday}
              loading={updateCompanyBranchAsyncIsLoading}
              disabled={updateCompanyBranchAsyncIsLoading}
            />
            <span>{t("onlineSalesSettings.disableSaturdays")}</span>
          </Flex>
          <Flex align="center" gap={12}>
            <Switch
              onChange={(value) => {
                setAvailabilityExceptions((current: any) => ({
                  ...current,
                  sunday: value,
                }));
              }}
              checked={availabilityExceptions.sunday}
              loading={updateCompanyBranchAsyncIsLoading}
              disabled={updateCompanyBranchAsyncIsLoading}
            />
            <span>{t("onlineSalesSettings.disableSundays")}</span>
          </Flex>
          <Flex align="start" vertical gap={12}>
            <span>{t("onlineSalesSettings.selectDates")}</span>
            <DatePicker
              multiple
              value={availabilityExceptions.exceptionDates.map((date) =>
                dayjs(date, "YYYY-MM-DD")
              )}
              maxTagCount="responsive"
              onChange={(e, e2) => {
                if (e) {
                  setAvailabilityExceptions((current: any) => ({
                    ...current,
                    exceptionDates: e2,
                  }));
                } else {
                  setAvailabilityExceptions((current: any) => ({
                    ...current,
                    exceptionDates: [],
                  }));
                }
              }}
              allowClear
              format="YYYY-MM-DD"
            />
          </Flex>
        </Flex>
      </Modal>

      <AgreementModal
        open={isAgreementModalOpen}
        onClose={() => setIsAgreementModalOpen(false)}
        onSuccess={() => {
          queryClient.invalidateQueries([`/company-branches`]);
        }}
      />
    </>
  );
};
